import { computed, ref } from 'vue';
import { injectNamedService } from '@patsnap/tahiti-core/ioc/inject';
import { useDisposes } from '@/composables/use-disposes';
import { TranslationSettingsService } from '@/services/_translation/service/translation-settings.service';
import { SettingType } from '@/services/_translation/type/translation.enum';
import type { TargetSetting, TargetVisibleSetting } from '@/services/_translation/type/translation.type';

export function useTranslationStates() {
    const expectedTranslationSettingsService = injectNamedService<TranslationSettingsService>(TranslationSettingsService, SettingType.EXPECTED);
    const currentTranslationSettingsService = injectNamedService<TranslationSettingsService>(TranslationSettingsService, SettingType.CURRENT);

    const { disposes } = useDisposes();

    const userPreferredLangOptions = ref(expectedTranslationSettingsService.getModelData());
    disposes.push(
        expectedTranslationSettingsService.onStatesChange(states => userPreferredLangOptions.value = states)
    );

    const currentLangOptions = ref(currentTranslationSettingsService.getModelData());
    disposes.push(
        currentTranslationSettingsService.onStatesChange(states => currentLangOptions.value = states)
    );

    const currentTranslateLanguages = computed(() => {
        return {
            left: currentLangOptions.value.left.lang,
            right: currentLangOptions.value.right.lang,
        }
    });

    const currentLanguageModes = computed(() => {
        return {
            left: currentLangOptions.value.left.langType,
            right: currentLangOptions.value.right.langType,
        }
    });

    const currentAlteration = computed(() => {
        return {
            left: currentLangOptions.value.left.isActivated,
            right: currentLangOptions.value.right.isActivated,
        }
    });

    const sideTransStatus = computed(() => {
        return {
            left: currentLangOptions.value.left.isVisible,
            right: currentLangOptions.value.right.isVisible,
        }
    });

    function setUserPreferredLangOptions(options: TargetSetting) {
        expectedTranslationSettingsService.setTranslation(options);
    }

    function switchTranslateLanguage(options: TargetSetting) {
        currentTranslationSettingsService.setTranslation(options);
    }

    function setSideTransStatus(options: TargetVisibleSetting) {
        currentTranslationSettingsService.setTranslationVisible(options);
    }

    function setUserPreferredSideTransStatus(options: TargetVisibleSetting) {
        expectedTranslationSettingsService.setTranslationVisible(options);
    }

    function syncSideTransStatusWithUserPreferred() {
        currentTranslationSettingsService.setTranslationVisible({
            side: 'left',
            isVisible: userPreferredLangOptions.value.left.isVisible,
        });
        currentTranslationSettingsService.setTranslationVisible({
            side: 'right',
            isVisible: userPreferredLangOptions.value.right.isVisible,
        });
    }

    return {
        /**
         * 当前翻译语言
         */
        currentTranslateLanguages,
        /**
         * 当前语言模式（默认/翻译/原文）
         */
        currentLanguageModes,
        /**
         * 左右视图的语言选项（翻译语言+预言模式）
         */
        userPreferredLangOptions,
        currentAlteration,
        sideTransStatus,
        switchTranslateLanguage,
        setUserPreferredLangOptions,
        setSideTransStatus,
        setUserPreferredSideTransStatus,
        syncSideTransStatusWithUserPreferred,
    };
}
